import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useAuth } from "../../../authentication/AuthProvider";
import {
  ApproveOrDeniedEPO,
  ApproveOrDeniedDirector,
} from "../../../services/expense-purchase-order";
import {
  ApproveRecording,
  SubmitApproval,
  VisitorApprovalViaMail,
} from "../../../services/attendance-change";
import { playNotificationSound, unlockNotificationSound } from "../../../utils/notificationSound";
import { ApproveEmployeeOfTheMonth } from "../../../services/human-resource";

const NOTIFICATION_MODES = {
  SOUND: "sound",
  MUTE: "mute",
};

const isApprovalPending = (item) => {
  return (
    item?.isApprovalPending === true ||
    item?.IsApprovalPending === true ||
    Number(item?.isApprovalPending) === 1 ||
    Number(item?.IsApprovalPending) === 1
  );
};

const isApprovalNotification = (item) => {
  const isApproval =
    item?.isApproval === true ||
    item?.IsApproval === true ||
    Number(item?.isApproval) === 1 ||
    Number(item?.IsApproval) === 1;

  const approvalRequired =
    item?.approvalRequired === true ||
    item?.ApprovalRequired === true ||
    Number(item?.approvalRequired) === 1 ||
    Number(item?.ApprovalRequired) === 1;

  const category = String(
    item?.category ?? item?.Category ?? ""
  )
    .trim()
    .toUpperCase();

  const actionCode = String(
    item?.actionCode ?? item?.ActionCode ?? ""
  )
    .trim()
    .toUpperCase();

  const actionType = Number(item?.actionType);

  const pending =
    item?.isApprovalPending === true ||
    item?.IsApprovalPending === true ||
    Number(item?.isApprovalPending) === 1 ||
    Number(item?.IsApprovalPending) === 1;

  /*
   * Dynamic approval detection:
   * 1) Prefer SQL NotificationConfig metadata.
   * 2) Keep ActionType 6 + pending as a safe compatibility fallback.
   *
   * This prevents an approval notification from appearing in
   * All Notifications merely because one config property was not
   * serialized by the API/client.
   */
  return (
    isApproval ||
    approvalRequired ||
    category === "APPROVAL" ||
    actionCode === "APPROVAL_REQUIRED" ||
    (actionType === 6 && pending)
  );
};

const getActionEndpoint = (notification) => {
  const stepCode = String(notification?.workflowStepCode || "")
    .trim()
    .toUpperCase();

  const directorSteps = ["HIGHER_AUTHORITY", "PRESIDENT"];

  return directorSteps.includes(stepCode)
    ? ApproveOrDeniedDirector
    : ApproveOrDeniedEPO;
};

const NotificationDropdown = () => {
  const {
    notifications,
    getNotifications,
    getEpoModulesCounts,
    notificationMode,
    setNotificationMode,
  } = useAuth();

  const [processingId, setProcessingId] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);

  const loading = notifications === null;

  const executeApprovalAction = async (item, isApprove) => {
    const handlerCode = String(
      item?.approvalHandlerCode ||
      item?.ApprovalHandlerCode ||
      ""
    )
      .trim()
      .toUpperCase();

    const moduleCode = String(
      item?.moduleCode ??
      item?.ModuleCode ??
      ""
    )
      .trim()
      .toUpperCase();

    const moduleType = Number(item?.moduleType);

    const effectiveHandlerCode =
      handlerCode ||
      (
        moduleCode === "VISITOR" || moduleType === 3
          ? "VISITOR_APPROVAL"
          : moduleCode === "FTO" || moduleType === 4
            ? "FTO_APPROVAL"
            : moduleCode === "EOM" || moduleType === 5
              ? "EOM_APPROVAL"
              : moduleCode === "RECORDING" || moduleType === 6
                ? "RECORDING_APPROVAL"
                : moduleCode === "EPO" || moduleType === 1
                  ? "EPO_APPROVAL"
                  : ""
      );

    const documentId =
      item?.documentId ||
      item?.epoId ||
      item?.ftoId ||
      item?.recordingId ||
      item?.RecordingId;

    switch (effectiveHandlerCode) {
      case "EOM_APPROVAL": {
        const stepCode = String(item?.workflowStepCode || "")
          .trim()
          .toUpperCase();

        let approvalType = "HR";

        if (stepCode === "SUPERVISOR") {
          approvalType = "Supervisor";
        } else if (stepCode.startsWith("MPC")) {
          approvalType = "MPC";
        }

        return ApproveEmployeeOfTheMonth({
          nomineeId: Number(documentId),
          isApproved: isApprove,
          remarks: "",
          approvalType,
          isVoid: false,
        });
      }

      case "VISITOR_APPROVAL":
        return VisitorApprovalViaMail({
          Id: Number(documentId),
          IsApprove: isApprove,
          UserId: Number(item?.userId),
        });

      case "FTO_APPROVAL":
        return SubmitApproval({
          FtoId: documentId,
          IsApprove: isApprove,
        });

      case "RECORDING_APPROVAL":
        return ApproveRecording({
          Id: Number(documentId),
          IsApprove: isApprove,
          ApprovedBy: Number(item?.userId),
        });

      case "EPO_APPROVAL":
        return getActionEndpoint(item)(
          documentId,
          isApprove
        );

      default:
        throw new Error(
          `No approval handler configured for ${effectiveHandlerCode || moduleCode || "this notification"}.`
        );
    }
  };

  const handleAction = async (item, isApprove) => {
    const id = item?.id;
    const documentId =
      item?.documentId ||
      item?.epoId ||
      item?.ftoId ||
      item?.recordingId ||
      item?.RecordingId;

    const approvalPending = isApprovalPending(item);
    const approval = isApprovalNotification(item);

    if (
      !id ||
      !documentId ||
      processingId ||
      !approvalPending ||
      !approval
    ) {
      return;
    }

    try {
      setProcessingId(id);

      const response = await executeApprovalAction(
        item,
        isApprove
      );

      if (response?.status >= 200 && response?.status < 300) {
        await Promise.all([
          getNotifications(),
          getEpoModulesCounts(),
        ]);
      }
    } catch (error) {
      console.error(
        "Notification approval action failed:",
        error
      );
    } finally {
      setProcessingId(null);
    }
  };

  const normalNotifications = React.useMemo(() => {
    if (!Array.isArray(notifications)) return [];

    return notifications
      .filter((item) => {
        const notificationEnabled =
          item?.notificationEnabled !== false &&
          item?.NotificationEnabled !== false &&
          Number(item?.notificationEnabled ?? item?.NotificationEnabled ?? 1) !== 0;

        const bellEnabled =
          item?.bellEnabled !== false &&
          item?.BellEnabled !== false &&
          Number(item?.bellEnabled ?? item?.BellEnabled ?? 1) !== 0;

        const approval = isApprovalNotification(item);
        const pending = isApprovalPending(item);
        const actionType = Number(item?.actionType);

        // Active ActionType 6 approvals belong only to Approval Required.
        const activeApproval =
          pending && (
            approval ||
            actionType === 6
          );

        return (
          notificationEnabled &&
          bellEnabled &&
          !activeApproval
        );
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [notifications]);

  const approvalNotifications = React.useMemo(() => {
    if (!Array.isArray(notifications)) return [];

    return notifications
      .filter((item) => {
        const notificationEnabled =
          item?.notificationEnabled !== false &&
          item?.NotificationEnabled !== false &&
          Number(item?.notificationEnabled ?? item?.NotificationEnabled ?? 1) !== 0;

        const bellEnabled =
          item?.bellEnabled !== false &&
          item?.BellEnabled !== false &&
          Number(item?.bellEnabled ?? item?.BellEnabled ?? 1) !== 0;

        const approval = isApprovalNotification(item);
        const actionType = Number(item?.actionType);
        const pending = isApprovalPending(item);

        return (
          notificationEnabled &&
          bellEnabled &&
          pending &&
          (approval || actionType === 6)
        );
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [notifications]);

  const visibleNotifications =
    activeTab === 0
      ? normalNotifications
      : approvalNotifications;

  const formatNotificationAmount = (item) => {
    const rawAmount = item?.epoAmount ?? item?.EpoAmount;

    if (rawAmount === null || rawAmount === undefined || rawAmount === "") {
      return null;
    }

    const amount = Number(rawAmount);

    if (!Number.isFinite(amount)) {
      return null;
    }

    // CurrencyType = 1 is treated as INR.
    // Keep other currencies as a plain formatted amount
    // until their exact enum mapping is required.
    if (Number(item?.epoCurrencyType) === 1) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(amount);
    }

    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatFtoDate = (value) => {
    if (!value) return null;

    const valueString = String(value).trim();

    // API format: DD-MM-YYYY HH:mm:ss AM/PM
    const match = valueString.match(
      /^(\d{2})-(\d{2})-(\d{4})/
    );

    if (match) {
      const [, day, month, year] = match;
      return `${day}/${month}/${year}`;
    }

    // Fallback for normal ISO date values
    const date = new Date(valueString);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString("en-GB");
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Typography
            variant="h3"
            fontWeight={600}
            sx={{ px: 2 }}
          >
            Notifications
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            sx={{
              mt: 0.75,
              minHeight: 42,
              width: "100%",

              "& .MuiTabs-flexContainer": {
                width: "100%",
              },

              "& .MuiTab-root": {
                flex: "1 1 50%",
                minWidth: 0,
                minHeight: 42,
                px: 0.75,
                py: 0.5,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.82rem",
                whiteSpace: "nowrap",
              },
            }}
          >
            <Tab
              label={
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                    whiteSpace: "nowrap",
                    flexWrap: "nowrap",
                  }}
                >
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    All Notifications
                  </Box>

                  <Chip
                    size="small"
                    label={normalNotifications.length}
                    sx={{
                      height: 19,
                      minWidth: 22,
                      flexShrink: 0,
                      borderRadius: 1.25,
                      "& .MuiChip-label": {
                        px: 0.55,
                        fontSize: "0.64rem",
                      },
                    }}
                  />
                </Stack>
              }
            />

            <Tab
              label={
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                    whiteSpace: "nowrap",
                    flexWrap: "nowrap",
                  }}
                >
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    Approval Required
                  </Box>

                  <Chip
                    size="small"
                    color={approvalNotifications.length > 0 ? "error" : "default"}
                    label={approvalNotifications.length}
                    sx={{
                      height: 19,
                      minWidth: 22,
                      flexShrink: 0,
                      borderRadius: 1.25,
                      "& .MuiChip-label": {
                        px: 0.55,
                        fontSize: "0.64rem",
                      },
                    }}
                  />
                </Stack>
              }
            />
          </Tabs>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
        >
          <FeatherIcon
            icon={
              notificationMode === NOTIFICATION_MODES.SOUND
                ? "bell"
                : "volume-x"
            }
            width={16}
            height={16}
          />

          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            Notification alerts
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={0.25}
        >
          {/* SOUND */}
          <IconButton
            size="small"
            color={
              notificationMode ===
                NOTIFICATION_MODES.SOUND
                ? "primary"
                : "default"
            }
            onClick={async () => {
              const mode =
                NOTIFICATION_MODES.SOUND;

              setNotificationMode(mode);

              // Unlock audio from the user's click
              // and immediately test the sound.
              const unlocked =
                await unlockNotificationSound();

              if (unlocked) {
                await playNotificationSound();
              }
            }}
            aria-label="Use notification sound"
            title="Sound"
          >
            <FeatherIcon
              icon="bell"
              width={17}
              height={17}
            />
          </IconButton>

          {/* MUTE */}
          <IconButton
            size="small"
            color={
              notificationMode ===
                NOTIFICATION_MODES.MUTE
                ? "primary"
                : "default"
            }
            onClick={() => {
              const mode =
                NOTIFICATION_MODES.MUTE;

              setNotificationMode(mode);

            }}
            aria-label="Mute notifications"
            title="Mute"
          >
            <FeatherIcon
              icon="volume-x"
              width={17}
              height={17}
            />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      {loading ? (
        <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : visibleNotifications.length === 0 ? (
        <Box sx={{ py: 5, px: 3, textAlign: "center" }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              mx: "auto",
              mb: 1.25,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "action.hover",
              color: "text.secondary",
            }}
          >
            <FeatherIcon icon="bell-off" width={20} height={20} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0
              ? "No notifications"
              : "No approvals are pending"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
          {visibleNotifications.map((item, index) => {
            const documentId =
              item?.documentId ||
              item?.epoId ||
              item?.ftoId ||
              item?.recordingId ||
              item?.RecordingId;

            const documentLabel = String(
              item?.moduleName ||
              item?.ModuleName ||
              item?.moduleCode ||
              item?.ModuleCode ||
              "Notification"
            );
            const notificationKey =
              `${documentId}-${item?.userId}`;

            const approvalPending =
              isApprovalPending(item);

            const isApproval = isApprovalNotification(item);

            const approvalHandlerCode = String(
              item?.approvalHandlerCode ||
              item?.ApprovalHandlerCode ||
              ""
            ).trim();

            const moduleCode = String(
              item?.moduleCode ?? item?.ModuleCode ?? ""
            )
              .trim()
              .toUpperCase();

            const moduleType = Number(item?.moduleType);

            const hasFallbackHandler =
              ["EPO", "FTO", "EOM", "VISITOR", "RECORDING"].includes(moduleCode) ||
              [1, 3, 4, 5, 6].includes(moduleType);

            /*
             * ApprovalHandlerCode is preferred. Existing moduleType
             * routing remains as a compatibility fallback because the
             * approval APIs are already working and are intentionally
             * not being changed.
             */
            const canAction =
              approvalPending &&
              isApproval &&
              Boolean(approvalHandlerCode || hasFallbackHandler);

            const epoAmount =
              moduleCode === "EPO"
                ? formatNotificationAmount(item)
                : null;

            const approvalStage =
              item?.approvalStage ||
              item?.ApprovalStage ||
              null;

            const ftoSupervisorStatus =
              item?.ftoSupervisorStatus ||
              item?.FtoSupervisorStatus ||
              null;

            const ftoSupervisorName =
              item?.ftoSupervisorName ||
              item?.FtoSupervisorName ||
              null;

            const ftoLeaveType =
              item?.ftoLeaveType ||
              item?.FtoLeaveType ||
              null;

            const ftoStartDate =
              item?.ftoStartDate ||
              item?.FtoStartDate ||
              null;

            const ftoEndDate =
              item?.ftoEndDate ||
              item?.FtoEndDate ||
              null;

            const parseNotificationDate = (value) => {
              if (!value) return null;

              const valueString = String(value).trim();

              // DD-MM-YYYY HH:mm:ss AM/PM
              const match = valueString.match(
                /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM))?/i
              );

              if (match) {
                const [
                  ,
                  day,
                  month,
                  year,
                  hours = "0",
                  minutes = "0",
                  seconds = "0",
                  ampm,
                ] = match;

                let hour = Number(hours);

                if (ampm) {
                  if (ampm.toUpperCase() === "PM" && hour !== 12) {
                    hour += 12;
                  }

                  if (ampm.toUpperCase() === "AM" && hour === 12) {
                    hour = 0;
                  }
                }

                return new Date(
                  Number(year),
                  Number(month) - 1,
                  Number(day),
                  hour,
                  Number(minutes),
                  Number(seconds)
                );
              }

              // ISO / normal API datetime fallback
              const date = new Date(valueString);

              return Number.isNaN(date.getTime()) ? null : date;
            };

            return (
              <React.Fragment key={item?.id || `${documentId}-${index}`}>
                <MenuItem
                  sx={{
                    px: 2,
                    py: 1.4,
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        flex: "0 0 auto",
                        mt: 0.25,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: approvalPending ? "primary.light" : "action.hover",
                        color: approvalPending ? "primary.main" : "text.secondary",
                      }}
                    >
                      <FeatherIcon
                        icon={approvalPending ? "check-circle" : "bell"}
                        width={17}
                        height={17}
                      />
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: approvalPending ? 700 : 600,
                          lineHeight: 1.4
                        }}
                      >
                        {item?.message ||
                          item?.Message ||
                          (documentId
                            ? `${documentLabel} #${documentId}`
                            : `${documentLabel} notification`)}
                      </Typography>

                      {approvalStage && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.45,
                            fontWeight: 700,
                            color: "primary.main",
                          }}
                        >
                          {approvalStage}
                        </Typography>
                      )}

                      {(ftoSupervisorName || ftoLeaveType || ftoStartDate) && (
                        <>
                          {ftoSupervisorName && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 0.45,
                                fontWeight: 700,
                                color: "text.primary",
                              }}
                            >
                              Supervisor: {ftoSupervisorName}
                              {ftoSupervisorStatus && (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.6,
                                    fontWeight: 700,
                                    color:
                                      String(ftoSupervisorStatus).toLowerCase() === "approved"
                                        ? "success.main"
                                        : String(ftoSupervisorStatus).toLowerCase() === "denied"
                                          ? "error.main"
                                          : "warning.main",
                                  }}
                                >
                                  ({ftoSupervisorStatus})
                                </Box>
                              )}
                            </Typography>
                          )}

                          {ftoLeaveType && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 0.35,
                                fontWeight: 600,
                              }}
                            >
                              Leave Type: {ftoLeaveType}
                            </Typography>
                          )}

                          {ftoStartDate && formatFtoDate(ftoStartDate) && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 0.35,
                                color: "text.secondary",
                              }}
                            >
                              Leave Date: {formatFtoDate(ftoStartDate)}
                              {ftoEndDate && formatFtoDate(ftoEndDate) !== formatFtoDate(ftoStartDate)
                                ? ` - ${formatFtoDate(ftoEndDate)}`
                                : ""}
                            </Typography>
                          )}
                        </>
                      )}

                      <Stack
                        direction="row"
                        spacing={1.25}
                        sx={{
                          mt: 0.65,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {epoAmount && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: "text.primary",
                            }}
                          >
                            {epoAmount}
                          </Typography>
                        )}

                        {item?.createdDate && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {(() => {
                              const date = parseNotificationDate(item.createdDate);

                              if (!date) return null;

                              const day = date.getDate();

                              const suffix =
                                day % 10 === 1 && day !== 11
                                  ? "st"
                                  : day % 10 === 2 && day !== 12
                                    ? "nd"
                                    : day % 10 === 3 && day !== 13
                                      ? "rd"
                                      : "th";

                              const month = date.toLocaleString("en-US", {
                                month: "short",
                              });

                              const year = date.getFullYear();

                              const time = date.toLocaleTimeString([], {
                                hour: "numeric",
                                minute: "2-digit",
                              });

                              return `${day}${suffix} ${month}, ${year}, ${time}`;
                            })()}
                          </Typography>
                        )}
                      </Stack>

                      {canAction && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={processingId === item?.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleAction(item, false);
                            }}
                          >
                            Deny
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={processingId === item?.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleAction(item, true);
                            }}
                          >
                            {processingId === item?.id ? "Processing..." : "Approve"}
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </MenuItem>
                {index < visibleNotifications.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default NotificationDropdown;
